import React, {FC, useEffect, useState} from "react";
import {useFetchedResource} from "../api/APIContext";
import {useNavigate, useParams} from "react-router";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faChevronRight,
  faEllipsisVertical,
} from "@fortawesome/free-solid-svg-icons";
import {Card} from "@traas/lib/src/components/Card";
import moment from "moment";
import {ProjectVisit, projectVisitStatusEnum} from "@traas/lib/src/api/dto/ProjectVisit";
import {useApiService} from "../../../lib/src/api/hooks/useApiService";
import config from "../config";
import {faLink, faLinkSlash} from "@fortawesome/pro-solid-svg-icons";
import {useModal} from "@traas/lib/src/components/layout/ModalProvider";
import {LinkProjectVisitTaskModal} from "../modals/ProjectVisits/LinkProjectVisitTaskModal";
import {getToken} from "@ventumsoftware/react-toolkit";
import {ProjectStatusLabel} from "../../../lib/src/components/data/ProjectStatusLabel";
import {Button} from "../../../lib/src/components/form/Button";
import {ValidationError} from "../../../lib/src/api/common/APIService";
import {MapWithMarkers} from "../../../lib/src/components/content/MapWithEmployeeMarkers";
import {ProjectVisitBadge} from "@traas/lib/src/components/data/ProjectVisitBadge"
import {UnlinkTaskFromProjectVisitModal} from "../modals/ProjectVisits/UnlinkTaskFromProjectVisitModal";
import {ProjectVisitContextMenu} from "../components/ProjectVisits/ProjectVisitContextMenu";
import {usePlanner} from "../planning/PlannerContext";
import {useRefreshEffect} from "../../../lib/src/realtime/RefreshController";

export const WerkbezoekenDetails: FC<{ }> = (props) => {
  const { projectVisit } = useParams();
  const navigate = useNavigate();
  const {projectVisitService} = useApiService(config, getToken, navigate)
  const {resource: visitDetails, reload: reloadVisitDetails} = useFetchedResource<ProjectVisit, string>((projectVisit) => projectVisitService.read(projectVisit))

  useRefreshEffect('project_visit', () => {
    if (projectVisit !== undefined) {
      reloadVisitDetails(projectVisit)
    }
  }, [projectVisit]);

  return <main className={"flex-1 overflow-y-scroll max-h-[calc(100vh-4rem)]"}>
    <div className={"mx-6 my-8"}>
      <div className={"max-w-(--breakpoint-lg) mx-auto"}>
        {visitDetails === null ? <>
          <div className={'mt-12 text-xl'}>
            Kan werkbezoek niet inladen of er is geen bestaand werkbezoek met dit id gevonden
          </div>
        </> : <>
          <PageHeader visit={visitDetails} onUpdated={() => projectVisit ? reloadVisitDetails(projectVisit) : null} />
          <ProjectVisitDetails visit={visitDetails} onUpdated={() => projectVisit ? reloadVisitDetails(projectVisit) : null} />
          {visitDetails.task && <ProjectVisitMap visit={visitDetails}/>}
        </>}
      </div>
    </div>
  </main>
}

const PageHeader: FC<{ visit: ProjectVisit, onUpdated?: () => void }> = (props) => {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true)

  return <div className={"flex-1 mb-4 space-y-3"}>
    <div className={"flex items-center justify-between h-12"}>

      <div className={"flex items-center space-x-3 text-xl font-bold"}>
        <div className={"hover:underline cursor-pointer text-brand-900"} onClick={() => navigate(`/werkbezoeken`)}>Werkbezoeken</div>
        <FontAwesomeIcon icon={faChevronRight} className={'text-lg'} />
        <div className={'text-slate-900'}>{props.visit?.name ?? '???'}</div>
      </div>

      <div className={'relative'}>
        <div className={"flex items-center bg-white border border-slate-200 rounded-sm px-2 py-2 hover:bg-slate-50 hover:cursor-pointer"} onClick={() => setIsCollapsed(false)}>
          <FontAwesomeIcon icon={faEllipsisVertical} />
        </div>
        {!isCollapsed && <div className={"fixed inset-0 z-10 bg-[#0001] dark:bg-[#0002]"} onClick={() => setIsCollapsed(true)}/>}
        {!isCollapsed && <ProjectVisitContextMenu projectVisit={props.visit} close={() => setIsCollapsed(true)} onUpdated={props.onUpdated} />}
      </div>
    </div>

  </div>

}

const ProjectVisitDetails: FC<{ visit: ProjectVisit, onUpdated?: () => void }> = (props) => {
  const navigate = useNavigate();
  const {projectVisitService} = useApiService(config, getToken, navigate)
  const [errors, setErrors] = useState<String[]>([])

  const unlinkTaskModal = useModal({title: 'Taak ontkoppelen', body: <UnlinkTaskFromProjectVisitModal visit={props.visit} onUpdated={props.onUpdated} />, size: 'md'})
  const linkVisitTaskModal = useModal({title: 'Taak koppelen', body: <LinkProjectVisitTaskModal visit={props.visit} onFinished={() => props.onUpdated?.()} />, size: 'md'})

  const editStatus = async () => {
    await projectVisitService.editStatus(props.visit.id, {status: projectVisitStatusEnum.Uitgevoerd})
      .catch(e => {
        if (e instanceof ValidationError) {
          setErrors(Object.values(e.errors).flat())
        }
        throw e
      })
    props.onUpdated?.()
  }

  return <Card title={'Details'}>
    <div className={'space-y-8'}>
      <div className={'grid grid-cols-5 gap-4'}>
        <div className={'col-span-1'}>
          <div className={'text-brand-900 font-semibold'}>Beschrijving</div>
          <div>{props.visit.name}</div>
        </div>
        <div className={'col-span-1'}>
          <div className={'text-brand-900 font-semibold'}>Datum</div>
          <div>{moment(props.visit.targetDate).format('DD-MM-YYYY')}</div>
        </div>
        <div className={'col-span-1'}>
          <div className={'text-brand-900 font-semibold'}>Type</div>
          <div>{props.visit.type}</div>
        </div>
        <div className={'col-span-1'}>
          <div className={'text-brand-900 font-semibold'}>Status</div>
          <ProjectVisitBadge visit={props.visit.status} />
        </div>
        {props.visit.status === 'Ingepland' && <Button type={'secondary'} size={'sm'} text={'Werkbezoek afronden'} icon={faCheckCircle} onClick={() => editStatus()} />}
      </div>
      <div className={'grid grid-cols-5 gap-4'}>
        {/* Project card */}
        <div className={'col-span-2'}>
          <div className={'mb-2 text-brand-900 font-semibold'}>Project</div>
          <ProjectCard visit={props.visit} />
        </div>
        <div className={'col-span-2'}>
          <div className={'flex flex-row space-x-3 mb-2 items-center'}>
            <div className={' text-brand-900 font-semibold'}>Taak</div>
            {props.visit.task ? <Button text={"Ontkoppelen"} icon={faLinkSlash} onClick={() => unlinkTaskModal.open()} size={"xs"} type={"danger"} />
              : <Button text={'Koppelen'} icon={faLink} onClick={() => linkVisitTaskModal.open()} size={"xs"} type={"secondary"} />}
          </div>
          <TaskCard visit={props.visit} onUpdated={props.onUpdated} />
        </div>
      </div>
    </div>
  </Card>
}

const ProjectVisitMap: FC<{ visit: ProjectVisit }> = (props) => {
  const navigate = useNavigate();
  const {projectService} = useApiService(config, getToken, navigate)
  const {resource: coordinates, reload} = useFetchedResource<{lat: number, lng: number}, string>((relationId) => projectService.getCoordinates(relationId))
  useEffect(() => {
    if (props.visit.project?.id) {
      reload(props.visit.project?.id)
    }
  }, [props.visit.project?.id]);

  return <Card title={'Locatie'}>
    <div className={'space-y-4'}>
      <div className={'flex flex-row space-x-4 items-center'}>
        <div>Adres: </div>
        <div>{props.visit.project?.deliveryAddressLine}, {props.visit.project?.deliveryCity}, {props.visit.project?.deliveryZipCode}</div>
      </div>
      <div className={'bg-slate-100 h-120 rounded-sm overflow-hidden mb-2 mt-8'}>
        {coordinates ? <>
          <MapWithMarkers center={coordinates} zoom={14} markers={[coordinates]} />
        </> : <></>}
      </div>
    </div>
  </Card>
}

const ProjectCard: FC<{ visit: ProjectVisit }> = (props) => {
  const {crmUrl} = config
  return props.visit?.project ? <div className={'flex items-center border border-slate-200 rounded-sm px-3 py-2 hover:bg-brand-50 hover:cursor-pointer'} onClick={() => {window.location.href = `${crmUrl}/projects/${props.visit.projectId}`}}>
      <div className={'flex-1'}>
        <div className={'flex flex-row space-x-4 items-center'}>
          <div className={'text-xs text-slate-700'}>#{props.visit.project?.projectNumber}</div>
          <ProjectStatusLabel projectStatus={props.visit.project.status} />
        </div>
        <div>{props.visit.project?.description}</div>
      </div>
      <div>
        <FontAwesomeIcon icon={faChevronRight} />
      </div>
    </div> : <div>

    </div>
}

const TaskCard: FC<{ visit: ProjectVisit, onUpdated?: () => void }> = (props) => {
  const {focusTask} = usePlanner()
  const navigate = useNavigate()
  const linkVisitTaskModal = useModal({title: 'Taak koppelen', body: <LinkProjectVisitTaskModal visit={props.visit} onFinished={() => props.onUpdated?.()} />, size: 'md'})

  return props.visit.task ? <div className={"col-span-1"} onClick={() => {
    if (props.visit.task?.startAt) {
      navigate('/')
      focusTask(props.visit.task)
    }
  }}>
    <div className={`flex border border-slate-200 rounded-sm py-2 px-3 min-h-16 ${props.visit.task.startAt ? 'hover:cursor-pointer hover:bg-brand-50' : ''}`}>
      <div className={'flex flex-col flex-1 '}>
        <div className={'flex flex-row space-x-2'}>
          <div className={'mr-2'}>#{props.visit.project?.projectNumber}</div>
          <div>{props.visit.task.description}</div>
        </div>

        {props.visit.task.startAt ? <div className={'flex items-center text-xs text-slate-800 space-x-2'}>
            <div>{moment(props.visit.task.startAt).format('DD-MM-YYYY')}</div>
            <div>{moment(props.visit.task.startAt).format('HH:mm')}</div>
            <span>{'-'}</span>
            <div>{moment(props.visit.task.startAt).add(props.visit.task.durationMinutes, 'minutes').format('HH:mm')}</div>
          </div> : <div className={'text-xs text-red-600'}>Taak niet ingepland</div>
        }

      </div>

      <div className={'flex items-center'}>
        <FontAwesomeIcon icon={faChevronRight} className={''} />
      </div>
    </div>
  </div> : <div className={'flex items-center justify-center border border-dashed border-brand-900 rounded-sm min-h-16 opacity-75 hover:opacity-100 hover:cursor-pointer'} onClick={() => linkVisitTaskModal.open()}>
    <div className={'border border-dashed border-brand-900 rounded-full px-2.5 py-2'}>
      <FontAwesomeIcon icon={faLink} />
    </div>
  </div>
}